import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import { productBrandsEndpoint } from './use-get-product-brands-api';

type TData = { id: number };
type TError = AxiosError;
type TVariables = { id: number };

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// DELETE ums/v1/cms/product-brands/:id
// The BE refuses deletion while the brand is used by live master products
// (409 with an explanatory message — surfaced by the error toast).
export function useDeleteProductBrandApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id }: TVariables): Promise<TData> => {
    try {
      const response = await http.delete(`${productBrandsEndpoint}/${id}`);
      return response.data.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${productBrandsEndpoint}/delete`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([productBrandsEndpoint]);
      queryClient.invalidateQueries([`${productBrandsEndpoint}/usage`]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
